<script setup lang="ts">
import FilterDialog from '@/components/FilterBuilder/FilterDialog.vue';
import TableHeader from '@/components/DataTable/TableHeader.vue';
import TableHeaderItem from '@/components/DataTable/TableHeaderItem.vue';
import TableHeaderPaginationGroup from '@/components/DataTable/TableHeaderPaginationGroup.vue';
import ListDisplayMenu from '@/components/DataTable/ListDisplayMenu.vue';
import { Input } from '@/components/ui/input';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { computed } from 'vue';
import type { IScope } from '@/types/filterBuilderTypes';

/**
 * Toolbar for the Playlists report — the same Filter / Display / Pagination
 * triad as {@link file:src/components/DataTable/AudioFileIssuesTableHeader.vue},
 * minus the rows/cards toggle (this report is table-only), plus the explicit
 * From/To report-window control (the date range isn't part of the IScope
 * filter, mirroring how Audio File Issues keeps `verified_at` separate).
 *
 * This page's saved views live in the section sidebar, under Reports, as a
 * list's do. The toolbar has no saved-view control.
 */
defineProps<{
  totalRecords?: number;
}>();

const fromDate = defineModel<string>('from', { required: true });
const toDate = defineModel<string>('to', { required: true });

const { setPageIndex, setFilter, storeKey, visiblePinnedColumns, visibleNonPinnedColumns } = usePersistentTable();
const visibleColumnCount = computed(() => visiblePinnedColumns.value.length + visibleNonPinnedColumns.value.length);



function paginationHandler(pageIndex: number) {
  setPageIndex(pageIndex);
}

function applyFilterHandler(parsedQuery: IScope) {
  setFilter(parsedQuery);
}
</script>

<template>
  <TableHeader>
    <template #left>
      <TableHeaderItem>
        <FilterDialog
          :key="storeKey"
          data-testid="playlist-switching-filter-dialog"
          @applyFilter="applyFilterHandler"
        />
      </TableHeaderItem>
      <!-- Display holds "Columns…", as the list toolbar's does. -->
      <TableHeaderItem>
        <ListDisplayMenu
          columns-label="Columns…"
          :columns-shown-count="visibleColumnCount"
          columns-test-id="playlist-switching-column-settings"
          :page-memory-key="storeKey"
        />
      </TableHeaderItem>
      <TableHeaderItem>
        <label class="flex items-center gap-1.5 text-xs">
          <span class="text-muted-foreground uppercase tracking-wide">From</span>
          <Input
            v-model="fromDate"
            type="date"
            class="h-8 w-36"
            data-testid="playlist-switching-from"
          />
        </label>
      </TableHeaderItem>
      <TableHeaderItem>
        <label class="flex items-center gap-1.5 text-xs">
          <span class="text-muted-foreground uppercase tracking-wide">To</span>
          <Input
            v-model="toDate"
            type="date"
            class="h-8 w-36"
            data-testid="playlist-switching-to"
          />
        </label>
      </TableHeaderItem>
    </template>

    <template #right>
      <TableHeaderItem class="min-w-max shrink-0">
        <TableHeaderPaginationGroup
          :total-records="totalRecords"
          total-label="playlists"
          @pagination="paginationHandler"
        />
      </TableHeaderItem>
    </template>
  </TableHeader>
</template>
